
import { useNavigate } from "react-router-dom";

import { Download, Maximize2, MapPin } from 'lucide-react';
import { ResourceDisclosureHeader } from './ResourceDisclosure';
import { Button } from '@/components/ui/button';
import { useAtom } from 'jotai';
import { authenticationAtom, mapQueryAtom } from "../State";
import { mapDataAtom } from "../State";
import { mapOptionsAtom } from "../State";


import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import MapAndImage from "../MapAndImage";
import { userAPI } from "../UserAPI";
import { getEntityType } from '@the-real-insight/tri-model';
import 'mapbox-gl/dist/mapbox-gl.css';
import { configuration } from '../Configuration';

// Shared rendering function that uses the proven download logic
const renderMapAndImageForCapture = async (mapResource: any): Promise<{canvas: HTMLCanvasElement, cleanup: () => void} | null> => {
    console.log('🗺️🖼️ [SHARED] Starting MapAndImage rendering for capture');
    
    // Create a temporary container for the React component
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '800px';
    tempContainer.style.height = '600px';
    tempContainer.style.visibility = 'hidden';
    document.body.appendChild(tempContainer);

    // Create a temporary map instance using the same MapAndImage component
    const tempMapRef = React.createRef<any>();
    
    // Render the MapAndImage component in the temporary container with Router context
    const root = ReactDOM.createRoot(tempContainer);
    root.render(
        <BrowserRouter>
            <MapAndImage
                ref={tempMapRef}
                query={null}
                data={mapResource.data}
                options={mapResource.options}
            />
        </BrowserRouter>
    );

    const cleanup = () => {
        try {
            root.unmount();
            document.body.removeChild(tempContainer);
        } catch (cleanupError) {
            console.warn('🗺️🖼️ [SHARED] Error during cleanup:', cleanupError);
        }
    };

    try {
        console.log('🗺️🖼️ [SHARED] Waiting for MapAndImage to render (8 seconds)...');
        await new Promise(resolve => setTimeout(resolve, 8000));

        // Get the Mapbox GL map instance from the ref
        const mapboxMap = tempMapRef.current?.getMap();
        if (!mapboxMap) {
            console.error('🗺️🖼️ [SHARED] Could not get Mapbox GL map instance');
            cleanup();
            return null;
        }

        console.log('✅ [SHARED] Mapbox GL map instance found:', mapboxMap);
        console.log('✅ [SHARED] Mapbox GL map loaded:', mapboxMap.loaded());
        console.log('✅ [SHARED] Mapbox GL map style loaded:', mapboxMap.isStyleLoaded());
        
        // Wait for map to be fully loaded if it isn't already (same as working download)
        if (!mapboxMap.loaded() || !mapboxMap.isStyleLoaded()) {
            console.log('🗺️🖼️ [SHARED] Waiting for map to finish loading...');
            await new Promise(resolve => {
                const checkLoaded = () => {
                    if (mapboxMap.loaded() && mapboxMap.isStyleLoaded()) {
                        console.log('✅ [SHARED] Map loading complete via polling');
                        resolve(undefined);
                    } else {
                        setTimeout(checkLoaded, 100);
                    }
                };
                checkLoaded();
            });
        }
        
        console.log('🗺️🖼️ [SHARED] Starting Mapbox GL canvas capture...');
        
        // Get canvas and export (same as working download)
        const canvas = mapboxMap.getCanvas();
        console.log('✅ [SHARED] Canvas obtained, size:', canvas.width, 'x', canvas.height);
        
        if (!canvas || canvas.width === 0 || canvas.height === 0) {
            console.error('🗺️🖼️ [SHARED] Canvas not properly initialized');
            cleanup();
            return null;
        }
        
        return { canvas, cleanup };
        
    } catch (error) {
        console.error('❌ [SHARED] Error in MapAndImage rendering:', error);
        cleanup();
        return null;
    }
};

// Extend Window interface to include our global state
declare global {
  interface Window {
    streamCompletionState?: {
      isStreamCompleted(): boolean;
      registerPendingResource(resourceId: string): void;
      markResourceCompleted(resourceId: string): void;
    };
    imageUploadTracker?: {
      registerUpload(resourceId: string, resourceType: string, uploadUrl?: string): void;
      updateStatus(resourceId: string, status: 'uploading' | 'success' | 'failed', finalUrl?: string, error?: string): void;
    };
  }
}

// Global state for expanded resources to persist across re-renders
const expandedResources = new globalThis.Map<string, boolean>();

/**
 * @param {{resource: any, stableKey?: string, resourceSelected?: (resource: any) => void}} props
 */
const MapAndImageResourceComponent = ({ resource, stableKey, resourceSelected: _resourceSelected }: { resource: any, stableKey?: string, resourceSelected?: (resource: any) => void }) => {
    const navigate = useNavigate();
    const [, setMapQuery] = useAtom(mapQueryAtom);
    const [, setMapData] = useAtom(mapDataAtom);
    const [, setMapOptions] = useAtom(mapOptionsAtom);
    const [_selectedEntity, setSelectedEntity] = useState(false);
    
    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => expandedResources.get(resourceKey) || false);
    
    const [authentication] = useAtom(authenticationAtom);
    const [mapResource, setMapResource] = useState(null) as any;
    const [imageGenerated, setImageGenerated] = useState(false);
    const mapRef = useRef<any>(null);
    const mapInstanceRef = useRef<any>(null);
    const shadowResourceCreated = useRef(false);
    const shadowStartTime = useRef<number | null>(null);
    
    // Create shadow resource for streaming image generation
    useEffect(() => {
        if (mapResource && !imageGenerated && resource._streamingContext && !shadowResourceCreated.current) {
            // Double-check DOM to prevent multiple shadow resources
            const existingShadow = document.getElementById(`shadow-mapAndImage-${mapResource._id}`);
            if (existingShadow) {
                return;
            }
            
            // Additional check: prevent shadow creation if resource already has an image
            if (mapResource.imageGenerated || mapResource.hasImage) {
                setImageGenerated(true);
                return;
            }
            
            shadowResourceCreated.current = true;
            shadowStartTime.current = Date.now();
            console.log(`🗺️🖼️ MapAndImage shadow rendering started: ${mapResource._id}`);
            createShadowMapAndImageResource();
        }
    }, [mapResource?._id, resource._streamingContext]);
    
    // Create shadow MapAndImage resource for streaming image generation
    const createShadowMapAndImageResource = () => {
        if (imageGenerated) return;
        
        // Prevent multiple shadow resources for the same resource
        const existingShadow = document.getElementById(`shadow-mapAndImage-${mapResource._id}`);
        if (existingShadow) return;
        
        // Create shadow container
        const shadowContainer = document.createElement('div');
        shadowContainer.id = `shadow-mapAndImage-${mapResource._id}`;
        shadowContainer.style.position = 'absolute';
        shadowContainer.style.left = '-9999px';
        shadowContainer.style.top = '-9999px';
        shadowContainer.style.width = '800px';
        shadowContainer.style.height = '600px';
        shadowContainer.style.visibility = 'hidden';
        shadowContainer.style.zIndex = '-1';
        document.body.appendChild(shadowContainer);
        
        // Create shadow MapAndImage component
        const tempMapRef = React.createRef<any>();
        
        // Render the MapAndImage component in the temporary container with Router context
        const root = ReactDOM.createRoot(shadowContainer);
        root.render(
            <BrowserRouter>
                <MapAndImage
                    ref={tempMapRef}
                    query={null}
                    data={mapResource.data}
                    options={mapResource.options}
                />
            </BrowserRouter>
        );
        
        // Wait for component to render and add event listeners
        setTimeout(() => {
            const mapboxMap = tempMapRef.current?.getMap();
            if (mapboxMap) {
                let uploadTriggered = false;
                
                // Add Mapbox GL event listeners for drawing completion
                const handleMapComplete = () => {
                    if (!uploadTriggered && !imageGenerated) {
                        uploadTriggered = true;
                        const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                        console.log(`🗺️🖼️ MapAndImage image submitted: ${mapResource._id} -> ${elapsedTime}ms`);
                        generateAndStoreImage();
                        setImageGenerated(true);
                        // Purge shadow resource after upload
                        setTimeout(() => {
                            if (shadowContainer.parentNode) {
                                shadowContainer.parentNode.removeChild(shadowContainer);
                            }
                        }, 1000);
                    }
                };
                
                // Listen for 'idle' event - fires when map has no ongoing animations or transitions
                mapboxMap.on('idle', handleMapComplete);
                
                // Also listen for 'load' event as backup
                mapboxMap.on('load', handleMapComplete);
            }
        }, 2000);
    };

    // All useEffect hooks must be called before any early returns
    useEffect(() => {
        let newResource: any = null;

        const call = async () => {
            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            // Ensure options is always an object, never null or undefined
            if (!newResource.options) {
                newResource.options = {};
            }

            // Instantiate entities in the new layers structure if present
            if (newResource.data && newResource.data.layers) {
                Object.values(newResource.data.layers).forEach((layer: any) => {
                    if (layer.elements) {
                        layer.elements.forEach((element: any) => {
                            if (element.entity && element.entity._type && typeof element.entity.getTypeClass !== 'function') {
                                try {
                                    const EntityClass = getEntityType(element.entity._type);
                                    if (EntityClass) {
                                        const entity = element.entity;
                                        // Generic normalization: Ensure both legacy 'shape' and GeoJSON 'geometry' are present if possible
                                        if (!entity.shape && entity.geometry?.coordinates?.[0]) {
                                            const coords = entity.geometry.type === 'Polygon' ? entity.geometry.coordinates[0] : entity.geometry.coordinates;
                                            if (Array.isArray(coords)) {
                                                entity.shape = coords.map((coord: any) => 
                                                    Array.isArray(coord) ? { lon: coord[0], lat: coord[1] } : coord
                                                );
                                            }
                                        } else if (!entity.geometry && entity.shape && Array.isArray(entity.shape)) {
                                            entity.geometry = {
                                                type: 'Polygon', // Default to Polygon for shapes
                                                coordinates: [entity.shape.map((point: any) => [point.lon || point.lng, point.lat])]
                                            };
                                        }

                                        element.entity = new (EntityClass as any)(element.entity);
                                    }
                                } catch (e) {
                                    // Ignore if type is not registered
                                    console.warn(`Failed to instantiate entity of type ${element.entity._type}:`, e);
                                }
                            }
                        });
                    }
                });
            }

            setMapResource(newResource);
        }

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !mapResource) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            // Cleanup any pending map instances
            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.off('error');
                    mapInstanceRef.current.off('load');
                    mapInstanceRef.current.off('idle');
                } catch (error) {
                    console.warn('🗺️🖼️ Error during map cleanup on unmount:', error);
                }
            }
        };
    }, []);

    // Export map as PNG using shared rendering logic
    const exportMapAsPNG = async () => {
        console.log('🧪 [DOWNLOAD] MapAndImageResource export started');
        
        if (!mapResource) {
            console.warn('❌ [DOWNLOAD] Map resource not available for export');
            return;
        }
        
        console.log('🧪 [DOWNLOAD] MapAndImage resource available:', {
            hasData: !!mapResource.data,
            dataKeys: mapResource.data ? Object.keys(mapResource.data) : [],
            hasOptions: !!mapResource.options
        });

        try {
            const result = await renderMapAndImageForCapture(mapResource);
            if (!result) {
                console.error('❌ [DOWNLOAD] Failed to render MapAndImage for capture');
                return;
            }
            
            const { canvas, cleanup } = result;
            const dataURL = canvas.toDataURL('image/png');
            console.log('✅ [DOWNLOAD] Data URL generated, length:', dataURL.length);
            
            // Download the image to user's computer
            console.log('🧪 [DOWNLOAD] Creating download link for local save...');
            const downloadLink = document.createElement('a');
            downloadLink.href = dataURL;
            downloadLink.download = `${mapResource?.title || 'mapAndImage'}.png`;
            
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            
            console.log('✅ [DOWNLOAD] MapAndImage download triggered successfully!');
            cleanup();
            
        } catch (error) {
            console.error('❌ [DOWNLOAD] Error exporting mapAndImage:', error);
        }
    };

    // Generate and store map and image in background with 3-attempt retry logic
    const generateAndStoreImage = async (retryCount = 0) => {
        if (!mapResource || !mapResource._id || imageGenerated) {
            return;
        }

        // Check if image already exists for this resource
        if (mapResource.imageGenerated || mapResource.hasImage) {
            setImageGenerated(true);
            return;
        }

        // Register upload in tracker
        if (window.imageUploadTracker) {
            window.imageUploadTracker.registerUpload(mapResource._id, 'mapAndImage');
        }

        try {
            // Use the exact same proven rendering logic as download
            const result = await renderMapAndImageForCapture(mapResource);
            if (!result) {
                throw new Error('Failed to render MapAndImage for capture');
            }
            
            const { canvas, cleanup } = result;
            const dataURL = canvas.toDataURL('image/png');
            
            // Validate the image data
            if (!dataURL || dataURL === 'data:,' || dataURL.length < 1000) {
                cleanup();
                throw new Error('Generated image is empty or too small');
            }
            
            // Update status to uploading
            if (window.imageUploadTracker) {
                window.imageUploadTracker.updateStatus(mapResource._id, 'uploading');
            }

            // Store image on server asynchronously
            const storeResult = await userAPI.storeResourceImage(authentication.customer, 'mapAndImage', mapResource._id, dataURL);
            
            if (storeResult && storeResult.success) {
                // Update status to success - use production API URL with correct /documents/ endpoint format
                const finalUrl = `${configuration.productionAPIURL}/documents/${authentication.customer}/resource-images/mapAndImage/${mapResource._id}.png`;
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(mapResource._id, 'success', finalUrl);
                }
                
                const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                console.log(`✅ MapAndImage upload successful: ${mapResource._id} -> ${finalUrl} (${elapsedTime}ms)`);
            } else {
                // Update status to failed
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(mapResource._id, 'failed', undefined, storeResult?.error || 'Upload failed');
                }
                console.error(`❌ MapAndImage upload failed: ${mapResource._id}`, storeResult?.error);
            }
            
            setImageGenerated(true);
            
            // Clean up rendering resources
            cleanup();
            
        } catch (error) {
            // Retry logic for generation - 3 attempts maximum
            if (retryCount < 2) {  // 0, 1, 2 = 3 attempts total
                console.log(`🗺️🖼️ Retrying mapAndImage generation in 2 seconds (attempt ${retryCount + 1}/3 failed)`);
                setTimeout(() => {
                    generateAndStoreImage(retryCount + 1);
                }, 2000);
            } else {
                console.error(`❌ MapAndImage upload failed after 3 retries: ${mapResource._id}`);
                
                // Update status to failed
                if (window.imageUploadTracker) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    window.imageUploadTracker.updateStatus(mapResource._id, 'failed', undefined, errorMessage);
                }
            }
        }
    };


    return <div className="box-border w-full">
        {/* Karte plus Bild: dieselbe Messung beim Aufbau wie bei der reinen
            Karte, also weiter bedingtes Rendern statt der Grid-Animation — nur
            Kopfzeile und Chevron folgen dem gemeinsamen Muster. */}
        <ResourceDisclosureHeader
            expanded={expanded}
            onToggle={(e) => {
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                const newExpanded = !expanded;
                setExpanded(newExpanded);
                // Store in global state to persist across re-renders
                expandedResources.set(resourceKey, newExpanded);
            }}
            testId="chat-resource-map-image-toggle"
            icon={<MapPin aria-hidden className="h-5 w-5 shrink-0" />}
            title={mapResource?.title}
            actions={
            <div className="flex shrink-0 justify-end">
                {mapResource && (
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        data-testid="chat-resource-map-image-download"
                        className="mr-0.5 h-8 w-8"
                        onClick={exportMapAsPNG}
                        title="Download map as PNG"
                    >
                        <Download size={16} />
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    data-testid="chat-resource-map-image-expand"
                    className="h-8 w-8"
                    title="Expand"
                    aria-label="Expand"
                    onClick={() => {
                        if (mapResource) {
                            setMapData(mapResource.data);
                            setMapQuery(mapResource.query);
                            setMapOptions(mapResource.options || {});
                            navigate("/map-and-image")
                        }
                    }}
                >
                    <Maximize2 size={16} />
                </Button>
            </div>
            }
        />
        {expanded && mapResource
            ?
            <div className="mt-[25px]">
                <MapAndImage ref={(ref) => {
                    mapRef.current = ref;
                    if (ref && ref.getMap) {
                        mapInstanceRef.current = ref.getMap();
                        
                        // No event listeners on visible map - only shadow resources handle uploads
                    }
                }} data={mapResource.data} query={null} options={mapResource.options || {}} setObject={(entity: any) => {
                    setSelectedEntity(entity);
                }}></MapAndImage>
            </div>
            :
            <></>
        }
        
    </div>
}

// Export without memoization to troubleshoot expansion issues
export const MapAndImageResource = MapAndImageResourceComponent;
